import { observable, computed, action } from 'mobx';
import { Address } from './address';
import { Model } from './model';
import { Validate } from '../validate';
import { round, roundedTotal } from '../math';
import { Order } from './order';
import { IRefundResponse } from 'src/services/sales-api';

export class Refund extends Model {

    @observable public fromAddress: Address = new Address({});
    @observable public toAddress: Address = new Address({});
    @observable public pickupFee: number = 150;
    @observable public pickupRequired: boolean = false;
    @observable public restockingFee: number = 0;
    @observable private order: Order;

    constructor(order: Order) {
        super();
        this.order = order;
    }

    @action
    public setPickupRequired(required: boolean) {
        this.pickupRequired = required;
    }

    @action
    public resetDefaulRestockingFee() {
        this.restockingFee = round(this.subTotalCredited * .30);
    }

    @computed
    public get subTotalCredited(): number {
        return round(this.order.productLines.map(p => p.totalCredited)
            .reduce((acc, curr) => acc + curr, 0));
    }

    @computed
    public get subTotalCreditedIncludingGst(): number {
        return round(this.order.productLines
            .map(p => roundedTotal(p.totalCredited))
            .reduce((acc, curr) => acc + curr, 0));
    }

    @computed
    public get totalCredited(): number {
        return this.subTotalCredited - this.restockingFee;
    }

    public calculateProductRestockingFee(
        productTotalCredited: number,
        applied: boolean,
        percentage: number
    ): number {
        if (!applied) return 0;
        const validPercentage = Math.min(100, Math.max(0, percentage));
        return round(productTotalCredited * validPercentage / 100);
    }

    public calculateTotalRestockingFee(restockingFees: number[]): number {
        return round(restockingFees.reduce((total, fee) => total + fee, 0));
    }

    public calculateProductRefundAmount(productTotalCredited: number, restockingFee: number): number {
        return round(productTotalCredited - Math.max(0, restockingFee));
    }

    public calculateCustomerCredit(restockingFee: number, freightAmount: number): number {
        const validRestockingFee = Math.max(0, restockingFee);
        const validFreightAmount = Math.max(0, freightAmount);
        return round(this.subTotalCredited - validRestockingFee - validFreightAmount);
    }

    public calculateCustomerCreditIncludingGst(customerCreditExGst: number): number {
        return roundedTotal(customerCreditExGst);
    }

    public customerCreditExceedsPaid(customerCreditExGst: number, amountPaid: number): boolean {
        return this.calculateCustomerCreditIncludingGst(customerCreditExGst) > Math.max(0, amountPaid);
    }

    public static fromResponse(r: IRefundResponse, order: Order) {
        const refund = new Refund(order);
        if (!r) return refund;
        refund.fromAddress = Address.fromResponse(r.fromAddress);
        refund.toAddress = Address.fromResponse(r.toAddress);
        refund.restockingFee = r.restockingFee;
        refund.pickupFee = r.pickupFee;
        refund.pickupRequired = r.pickupRequired;
        return refund;
    }

    protected validators: { [name: string]: (val: any) => boolean } = {
        "pickupFee": (val: number) => {
            this.errors.pickupFee = Validate.decimal(val) && val > 0
                ? null : "Must be more than 0";
            return this.errors.pickupFee === null;
        },
        "restockingFee": (val: number) => {
            this.errors.restockingFee = Validate.decimal(val) && val >= 0
                ? null : "Must be more than 0";
            return this.errors.restockingFee === null;
        },
        "fromAddress": () => {
            if (!this.pickupRequired) return true;
            return this.fromAddress.validate();
        },
        "toAddress": () => {
            if (!this.pickupRequired) return true;
            return this.toAddress.validate();
        },
        "order": () => {
            if (!this.order.isRefunding) return true;
            this.errors.hasCredits = this.order.productLines.some(p => p.creditQuantity > 0)
                ? null : "Credit amount is $0. Select units to credit against products";
            return this.errors.hasCredits === null;
        }
    }
}
